import { useState, useEffect } from 'react';
import { X, Copy, Check, Pencil, Send, ExternalLink, Loader2, Trash2, ImagePlus, Download, RefreshCw } from 'lucide-react';
import { coworkApi, API_BASE_URL, authHeaders } from '../api/coworkApi';

const PLATFORM_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  linkedin: { label: 'LinkedIn', color: '#0a66c2', bg: '#0a66c215' },
  instagram: { label: 'Instagram', color: '#e1306c', bg: '#e1306c15' },
  twitter: { label: 'X / Twitter', color: '#1d9bf0', bg: '#1d9bf015' },
  tiktok: { label: 'TikTok', color: '#010101', bg: '#01010110' },
  facebook: { label: 'Facebook', color: '#1877f2', bg: '#1877f215' },
  generic: { label: 'Content', color: '#667085', bg: '#66708515' },
};

interface ContentItem {
  id: string;
  teamId: string;
  agentId: string;
  platform: string;
  title: string;
  body: string;
  hashtags: string[];
  visualDirection?: string;
  postType: string;
  status: string;
  editedBody?: string;
  createdAt: string;
}

interface ContentDetailModalProps {
  contentId: string;
  onClose: () => void;
  onUpdated: () => void;
}

export function ContentDetailModal({ contentId, onClose, onUpdated }: ContentDetailModalProps) {
  const [item, setItem] = useState<ContentItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [copied, setCopied] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const [publishError, setPublishError] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasLinkedIn, setHasLinkedIn] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Visual generation state
  const [generatingVisual, setGeneratingVisual] = useState(false);
  const [visualUrl, setVisualUrl] = useState<string | null>(null);
  const [visualError, setVisualError] = useState('');
  const [visualPrompt, setVisualPrompt] = useState('');
  const [showVisualPrompt, setShowVisualPrompt] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE_URL}/v1/content-queue/${contentId}`, {
        headers: authHeaders(),
      }).then(r => r.json()) as Promise<ContentItem>,
      coworkApi.integrations.list().catch(() => ({ integrations: [] })),
    ]).then(([data, intData]) => {
      setItem(data);
      setEditText(data.editedBody || data.body);
      setPublished(data.status === 'published');
      setHasLinkedIn(intData.integrations.some((i: { provider: string }) => i.provider === 'linkedin'));

      // Check if visualDirection contains a previously generated image
      if (data.visualDirection) {
        try {
          const parsed = JSON.parse(data.visualDirection);
          if (parsed.imageUrl) {
            setVisualUrl(`${API_BASE_URL}${parsed.imageUrl}`);
            setVisualPrompt(parsed.prompt || '');
          } else {
            setVisualPrompt(data.visualDirection);
          }
        } catch {
          setVisualPrompt(data.visualDirection);
        }
      }
    }).catch((err) => {
      console.error('[content-detail] Failed to load:', err);
    }).finally(() => setLoading(false));
  }, [contentId]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
        <div className="bg-white rounded-2xl p-8 shadow-2xl" onClick={e => e.stopPropagation()}>
          <Loader2 className="w-6 h-6 animate-spin text-[#98a2b3]" />
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
        <div className="bg-white rounded-2xl p-8 shadow-2xl text-center space-y-3" onClick={e => e.stopPropagation()}>
          <p className="text-[14px] text-[#344054]" style={{ fontWeight: 500 }}>Content not found</p>
          <p className="text-[12px] text-[#98a2b3]">This content may have been deleted.</p>
          <button onClick={onClose} className="text-[12px] px-4 py-2 rounded-lg bg-gray-100 text-[#344054] hover:bg-gray-200 transition-colors" style={{ fontWeight: 500 }}>
            Close
          </button>
        </div>
      </div>
    );
  }

  const platform = PLATFORM_CONFIG[item.platform] || PLATFORM_CONFIG.generic;
  const displayBody = item.editedBody || item.body;

  const handleCopy = () => {
    const text = displayBody + (item.hashtags.length > 0 ? '\n\n' + item.hashtags.map(h => `#${h}`).join(' ') : '');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await coworkApi.contentQueue.update(item.id, { editedBody: editText });
      setItem({ ...item, editedBody: editText });
      setEditing(false);
      onUpdated();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handlePublishDirect = async () => {
    setPublishing(true);
    setPublishError('');
    try {
      const result = await coworkApi.contentQueue.publish(item.id);
      if (result.error) {
        setPublishError(result.error);
      } else {
        setPublished(true);
        setItem({ ...item, status: 'published' });
        onUpdated();
      }
    } catch {
      setPublishError('Publish failed');
    }
    setPublishing(false);
  };

  const handlePublishShareUrl = () => {
    const text = encodeURIComponent(
      displayBody + (item.hashtags.length > 0 ? '\n\n' + item.hashtags.map(h => `#${h}`).join(' ') : '')
    );
    const platformUrls: Record<string, string> = {
      linkedin: `https://www.linkedin.com/feed/?shareActive=true&text=${text}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?quote=${text}`,
    };
    const url = platformUrls[item.platform] || platformUrls.linkedin;
    window.open(url, '_blank', 'noopener,noreferrer');
    setPublished(true);
    coworkApi.contentQueue.update(item.id, { status: 'published' }).catch(() => {});
    onUpdated();
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await coworkApi.contentQueue.delete(item.id);
      onUpdated();
      onClose();
    } catch { /* ignore */ }
    setDeleting(false);
  };

  const handleGenerateVisual = async () => {
    setGeneratingVisual(true);
    setVisualError('');
    try {
      const result = await coworkApi.contentQueue.generateVisual(item.id, visualPrompt || undefined);
      if (result.error) {
        setVisualError(result.error);
      } else if (result.imageUrl) {
        setVisualUrl(`${API_BASE_URL}${result.imageUrl}`);
      }
    } catch {
      setVisualError('Failed to generate visual');
    }
    setGeneratingVisual(false);
  };

  const handleDownloadVisual = async () => {
    if (!visualUrl) return;
    try {
      const res = await fetch(visualUrl);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${item.title.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}-visual.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch { window.open(visualUrl, '_blank'); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
          <span
            className="text-[11px] px-2.5 py-1 rounded-full"
            style={{ background: platform.bg, color: platform.color, fontWeight: 600 }}
          >
            {platform.label}
          </span>
          <span className="text-[14px] text-[#101828] flex-1 truncate" style={{ fontWeight: 600 }}>
            {item.title}
          </span>
          {item.postType !== 'text' && (
            <span className="text-[10px] text-[#98a2b3] bg-gray-50 px-2 py-0.5 rounded">
              {item.postType}
            </span>
          )}
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${
            item.status === 'published' ? 'bg-green-50 text-green-600' :
            item.status === 'saved' ? 'bg-green-50 text-green-500' :
            'bg-gray-50 text-[#98a2b3]'
          }`} style={{ fontWeight: 600 }}>
            {item.status === 'published' ? 'Published' : item.status === 'saved' ? 'Saved' : 'Draft'}
          </span>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 transition-colors text-[#98a2b3]">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Post text */}
          {editing ? (
            <div className="space-y-3">
              <textarea
                value={editText}
                onChange={e => setEditText(e.target.value)}
                className="w-full text-[13px] text-[#344054] leading-relaxed bg-gray-50 rounded-xl p-4 border border-gray-200 outline-none focus:border-gray-300 resize-none"
                rows={Math.min(editText.split('\n').length + 3, 18)}
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="text-[12px] px-4 py-2 rounded-lg bg-[#101828] text-white hover:bg-[#1d2939] transition-colors disabled:opacity-50"
                  style={{ fontWeight: 500 }}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
                <button
                  onClick={() => { setEditing(false); setEditText(item.editedBody || item.body); }}
                  className="text-[12px] px-4 py-2 rounded-lg text-[#667085] hover:bg-gray-50 transition-colors"
                  style={{ fontWeight: 500 }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p className="text-[13px] text-[#344054] leading-relaxed whitespace-pre-line">
              {displayBody}
            </p>
          )}

          {/* Hashtags */}
          {item.hashtags.length > 0 && !editing && (
            <div className="flex flex-wrap gap-1.5">
              {item.hashtags.map((tag, i) => (
                <span key={i} className="text-[11px] px-2 py-0.5 rounded-lg" style={{ color: platform.color, background: platform.bg }}>
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* ── Visual Section ── */}
          {!editing && (
            <div className="border border-gray-100 rounded-xl overflow-hidden">
              {/* Generated image */}
              {visualUrl && (
                <div className="relative group">
                  <img
                    src={visualUrl}
                    alt="Generated visual"
                    className="w-full"
                  />
                  {/* Overlay actions */}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={handleDownloadVisual}
                      className="p-1.5 rounded-lg bg-black/60 text-white hover:bg-black/80 transition-colors"
                      title="Download"
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => { setShowVisualPrompt(true); }}
                      className="p-1.5 rounded-lg bg-black/60 text-white hover:bg-black/80 transition-colors"
                      title="Regenerate"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Generation controls */}
              <div className="p-3 bg-gray-50">
                {generatingVisual ? (
                  <div className="flex items-center gap-2 justify-center py-3">
                    <Loader2 className="w-4 h-4 animate-spin text-[#667085]" />
                    <span className="text-[12px] text-[#667085]" style={{ fontWeight: 500 }}>
                      Generating artwork with AI…
                    </span>
                  </div>
                ) : visualError ? (
                  <div className="space-y-2">
                    <p className="text-[11px] text-red-500 text-center">{visualError}</p>
                    <button
                      onClick={handleGenerateVisual}
                      className="w-full flex items-center justify-center gap-1.5 text-[12px] px-3 py-2 rounded-lg text-[#667085] hover:bg-gray-100 transition-colors"
                      style={{ fontWeight: 500 }}
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      Try again
                    </button>
                  </div>
                ) : showVisualPrompt || !visualUrl ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <ImagePlus className="w-4 h-4 text-[#98a2b3]" />
                      <span className="text-[11px] text-[#98a2b3]" style={{ fontWeight: 600 }}>
                        {visualUrl ? 'Regenerate visual' : 'Generate visual'}
                      </span>
                    </div>
                    <input
                      type="text"
                      value={visualPrompt}
                      onChange={e => setVisualPrompt(e.target.value)}
                      placeholder="Describe the visual you want (or leave empty for auto)"
                      className="w-full text-[12px] text-[#344054] bg-white rounded-lg px-3 py-2 border border-gray-200 outline-none focus:border-gray-300 placeholder:text-[#d0d5dd]"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleGenerateVisual}
                        className="flex items-center gap-1.5 text-[12px] px-4 py-2 rounded-lg bg-[#101828] text-white hover:bg-[#1d2939] transition-colors"
                        style={{ fontWeight: 500 }}
                      >
                        <ImagePlus className="w-3.5 h-3.5" />
                        Generate
                      </button>
                      {showVisualPrompt && visualUrl && (
                        <button
                          onClick={() => setShowVisualPrompt(false)}
                          className="text-[12px] px-3 py-2 rounded-lg text-[#667085] hover:bg-gray-100 transition-colors"
                          style={{ fontWeight: 500 }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-[#98a2b3]" style={{ fontWeight: 500 }}>
                      AI-generated visual • Brand logo applied
                    </span>
                    <button
                      onClick={handleDownloadVisual}
                      className="flex items-center gap-1 text-[11px] text-[#667085] hover:text-[#344054] transition-colors"
                      style={{ fontWeight: 500 }}
                    >
                      <Download className="w-3 h-3" />
                      Download
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center gap-2 px-6 py-4 border-t border-gray-100">
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 text-[12px] px-3 py-2 rounded-xl text-[#667085] hover:bg-gray-50 transition-all"
            style={{ fontWeight: 500 }}
          >
            {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
          {!editing && !published && (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-1.5 text-[12px] px-3 py-2 rounded-xl text-[#667085] hover:bg-gray-50 transition-all"
              style={{ fontWeight: 500 }}
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
          )}
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex items-center gap-1.5 text-[12px] px-3 py-2 rounded-xl text-red-400 hover:bg-red-50 transition-all disabled:opacity-50"
            style={{ fontWeight: 500 }}
          >
            <Trash2 className="w-3.5 h-3.5" />
            {deleting ? 'Deleting…' : 'Delete'}
          </button>

          <div className="flex-1" />

          {publishError && (
            <span className="text-[11px] text-red-500 mr-2">{publishError}</span>
          )}

          {published ? (
            <span className="flex items-center gap-1.5 text-[12px] px-4 py-2 rounded-xl text-green-600 bg-green-50" style={{ fontWeight: 600 }}>
              <Check className="w-3.5 h-3.5" />
              Published
            </span>
          ) : publishing ? (
            <span className="flex items-center gap-1.5 text-[12px] px-4 py-2 rounded-xl text-[#667085]" style={{ fontWeight: 500 }}>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Publishing…
            </span>
          ) : hasLinkedIn && item.platform === 'linkedin' ? (
            <button
              onClick={handlePublishDirect}
              className="flex items-center gap-1.5 text-[12px] px-4 py-2 rounded-xl text-white hover:opacity-90 transition-all"
              style={{ fontWeight: 600, background: '#0a66c2' }}
            >
              <Send className="w-3.5 h-3.5" />
              Publish to LinkedIn
            </button>
          ) : (
            <button
              onClick={handlePublishShareUrl}
              className="flex items-center gap-1.5 text-[12px] px-4 py-2 rounded-xl text-white hover:opacity-90 transition-all"
              style={{ fontWeight: 600, background: PLATFORM_CONFIG[item.platform]?.color || '#0a66c2' }}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open in {platform.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
